// bootStrapOptions must match the logic in OwaPublicPathPlugin
declare var self: WorkerGlobalScope & {
    bootstrapOptions: {
        scriptBaseUrl?: string;
    };
    trustedTypes: TrustedTypePolicyFactory | undefined;
    bootFlights: string[];
};

export interface WorkerOptions {
    asyncLoad?: boolean;
    maxAttempts?: number;
    bootFlights?: string[];
}

// this function is used as a template string for worker code
// it should not be called directly or have any external dependencies
// the contract for a worker loading from a javascript URL is synchronous, so this call must not be async
export const workerTemplate = (
    scriptName: string,
    scriptPath: string,
    backupScriptPath: string,
    { asyncLoad = false, maxAttempts = 3, bootFlights = [] }: WorkerOptions = {}
): void => {
    const owaPublicPath = /^OwaPublicPath/;
    const url = scriptPath + scriptName;

    // The OwaPublicPathPlugin webpack plugin will assign
    //    __webpack_require__.p = self.bootstrapOptions.scriptBaseUrl
    // when the first webpack code loads.  So, set self.bootstrapOptions.scriptBaseUrl now
    self.bootstrapOptions = self.bootstrapOptions || {};
    self.bootstrapOptions.scriptBaseUrl = scriptPath;
    self.bootFlights = bootFlights;

    self.onunhandledrejection = (e: PromiseRejectionEvent) => {
        // do not output to console
        e.preventDefault();
        const errorName = e.reason?.name;
        if (errorName !== 'DatabaseClosedError') {
            // rethrow back to the main thread
            throw e.reason?.stack
                ? // cross origin stacks are not preserved in onerror
                  // so we need to flatten them
                  e.reason.name + ': ' + e.reason.message + '\n' + e.reason.stack
                : e.reason;
        }
    };

    let workerPolicy: Pick<TrustedTypePolicy, 'createScriptURL'> | undefined;
    if (self.trustedTypes) {
        workerPolicy = self.trustedTypes.createPolicy('workerPolicy', {
            createScriptURL: (input: string): string =>
                input.replace(owaPublicPath, currentScriptPath),
        });
    }

    let currentScriptPath = scriptPath;
    // override "importScripts" function to use the custom public path
    const _importScripts = self.importScripts;
    self.importScripts = (...scripts: (string | URL)[]) =>
        _importScripts.apply(
            self,
            scripts.map(s => {
                // replace the public path with the current script path
                if (
                    workerPolicy &&
                    // we don't want to trust previously untrusted URLs
                    self.trustedTypes?.isScriptURL(s) &&
                    owaPublicPath.test(s.toString())
                ) {
                    return workerPolicy.createScriptURL(s.toString());
                }
                return s;
            }) as string[]
        );

    const messageQueue: Event[] = [];
    self.addEventListener('message', queueMessage);

    if (asyncLoad) {
        setTimeout(loadWorker);
    } else {
        loadWorker();
    }

    function queueMessage(event: Event) {
        messageQueue.push(event);
    }

    function loadWorker() {
        try {
            loadScript(workerPolicy?.createScriptURL(url) ?? url, maxAttempts);
        } catch {
            // load from backup CDN
            currentScriptPath = backupScriptPath;
            const backupUrl = url.replace(scriptPath, backupScriptPath);
            loadScript(workerPolicy?.createScriptURL(backupUrl) ?? backupUrl, maxAttempts);
        }
    }

    function loadScript(script: string | TrustedScriptURL, leftAttempts: number) {
        try {
            self.performance?.mark('WorkerEntry_LS');
            self.importScripts(script as string);
            self.performance?.mark('WorkerEntry_LE');
            // replay any queued messages
            self.removeEventListener('message', queueMessage);
            messageQueue.forEach(event => self.dispatchEvent(event));
            messageQueue.length = 0;
        } catch (e) {
            // retry
            if (--leftAttempts > 0) {
                loadScript(script, leftAttempts);
            }

            throw e;
        }
    }
};
